import React, { useMemo, useState } from "react";
import "./Contact.css";
import { useLocalContext } from "../../context/LocalContext";

const Contact = () => {
  const { webinfo = {} } = useLocalContext();

  const brand = {
    name: webinfo.name || "Your Brand",
    email: webinfo.email || "hello@example.com",
    phone: webinfo.phone || "+91 80000 00000",
    phonecall: webinfo.phonecall || "+918000000000",
    address: webinfo.address || "Remote-first • Working with clients across cities",
    whatsapp: webinfo.whatsapp || "https://wa.me/918000000000",
    mapEmbed:
      webinfo.mapEmbed ||
      "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d241317.11609915975!2d72.74109703785945!3d19.082197839145994!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7b63a9d90e14b%3A0x838fe179b1bdc674!2sMumbai!5e0!3m2!1sen!2sin!4v1719999999999",
    socials: webinfo.socials || {
      x: "#",
      instagram: "#",
      facebook: "#",
      linkedin: "#",
    },
    hours: webinfo.hours || "Mon–Sat · 10:00–19:00 IST",
    sla: "Most replies within a few business hours",
  };

  const topics = useMemo(
    () => [
      "New website or landing page",
      "Run or fix Google / Meta ads",
      "Show up better on Google search (SEO)",
      "Increase enquiries from my current traffic",
      "Monthly marketing support",
      "Other / not sure yet",
    ],
    []
  );

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    topic: "",
    message: "",
    consent: false,
    honey: "",
  });
  const [status, setStatus] = useState({ type: "idle", msg: "" });

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((s) => ({ ...s, [name]: type === "checkbox" ? checked : value }));
  };

  const validate = () => {
    if (!form.name.trim()) return "Please enter your full name.";
    if (!form.email.trim() || !/^\S+@\S+\.\S+$/.test(form.email))
      return "Please enter a valid email.";
    if (!form.message.trim())
      return "Tell us a bit about your business and what you want help with.";
    if (!form.consent) return "Please tick the box to allow us to contact you.";
    if (form.honey) return "Spam detected.";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: "idle", msg: "" });

    const err = validate();
    if (err) return setStatus({ type: "error", msg: err });

    try {
      setStatus({ type: "loading", msg: "Sending your message…" });

      // Hook into your backend / form endpoint here
      // await fetch("/api/contact", {
      //   method: "POST",
      //   headers: { "Content-Type": "application/json" },
      //   body: JSON.stringify(form),
      // });

      await new Promise((r) => setTimeout(r, 900));

      setStatus({
        type: "success",
        msg:
          "Thank you for reaching out. Your message is in our inbox — we usually reply with next steps within a few working hours.",
      });
      setForm({
        name: "",
        email: "",
        phone: "",
        topic: "",
        message: "",
        consent: false,
        honey: "",
      });
    } catch (er) {
      setStatus({
        type: "error",
        msg:
          "Something went wrong while sending your message. Please try again or use WhatsApp for a quicker response.",
      });
    }
  };

  return (
    <section className="c2" aria-label="Contact section">
      <div className="c2-bg" aria-hidden="true" />

      <div className="c2-wrap">
        {/* Header */}
        <header className="c2-head">
          <p className="c2-kicker">Let’s talk about your marketing</p>
          <h1>Tell us how you get customers. We’ll help you get more.</h1>
          <p className="c2-sub">
            Whether you need a clearer website, better-performing ads, or to show up higher on
            Google, start with a simple message. We’ll reply in plain language with what we
            recommend and what it might cost — no pressure to start right away.
          </p>

          <div className="c2-badges" aria-hidden="true">
            <span className="badge">Websites · Google & Meta ads · SEO</span>
            <span className="badge">{brand.sla}</span>
          </div>
        </header>

        {/* Layout */}
        <div className="c2-grid">
          {/* LEFT – Contact options & context */}
          <aside className="c2-info">
            <div className="info-card main-card">
              <h2>Talk to a real person</h2>
              <p>
                Prefer a quick chat instead of a long form? Reach us during{" "}
                <strong>{brand.hours}</strong>. We’ll ask a few light questions about your
                business and what “good results” mean for you.
              </p>

              <ul className="contact-list">
                <li>
                  <span className="label">Call</span>
                  <a href={`tel:${brand.phonecall}`}>{brand.phone}</a>
                </li>
                <li>
                  <span className="label">Email</span>
                  <a href={`mailto:${brand.email}`}>{brand.email}</a>
                </li>
                <li>
                  <span className="label">Location</span>
                  <span>{brand.address}</span>
                </li>
              </ul>

              <div className="cta-row">
                <a
                  className="btn btn-primary"
                  href={brand.whatsapp}
                  target="_blank"
                  rel="noreferrer"
                >
                  Chat on WhatsApp
                </a>
                <a className="btn btn-ghost" href={`mailto:${brand.email}`}>
                  Email our team
                </a>
              </div>

              <div className="info-grid">
                <div className="info-pill">
                  <span className="pill-label">Typical work</span>
                  <span className="pill-value">
                    Websites, landing pages, ads & SEO for small businesses
                  </span>
                </div>
                <div className="info-pill">
                  <span className="pill-label">Common goals</span>
                  <span className="pill-value">
                    More calls, enquiries and bookings from the same or lower ad spend
                  </span>
                </div>
                <div className="info-pill">
                  <span className="pill-label">How we work</span>
                  <span className="pill-value">
                    Simple plans, clear prices, no long-term lock-in
                  </span>
                </div>
              </div>
            </div>

            <div className="info-card secondary-card">
              <h3>A few details that really help</h3>
              <ul className="bullets">
                <li>Your current website link (if you have one)</li>
                <li>
                  How you get customers today (referrals, walk-ins, Instagram, Google, etc.)
                </li>
                <li>
                  What you’d like to see in the next 3–6 months (more calls, more bookings, more
                  sales, etc.)
                </li>
              </ul>

              <div className="map-block">
                <div className="map-wrap">
                  <iframe
                    title="Office Location"
                    src={brand.mapEmbed}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
                <small className="map-note">
                  Remote-first team working with local, national and international clients over
                  calls, video and WhatsApp.
                </small>
              </div>
            </div>
          </aside>

          {/* RIGHT – Form */}
          <div className="c2-form">
            <div className="form-head">
              <h2>Tell us about your business</h2>
              <p>
                3–5 simple answers are enough. We’ll look at your situation and reply with what
                we’d fix first: website, ads, SEO or something else.
              </p>
            </div>

            <form onSubmit={handleSubmit} noValidate>
              {/* Honeypot */}
              <input
                type="text"
                name="honey"
                value={form.honey}
                onChange={onChange}
                className="hp"
                autoComplete="off"
                tabIndex="-1"
                aria-hidden="true"
              />

              <div className="row">
                <div className="field">
                  <label htmlFor="name">Full name</label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    placeholder="Jane Doe"
                    value={form.name}
                    onChange={onChange}
                    required
                  />
                </div>

                <div className="field">
                  <label htmlFor="email">Email</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="jane@business.com"
                    value={form.email}
                    onChange={onChange}
                    required
                  />
                </div>
              </div>

              <div className="row">
                <div className="field">
                  <label htmlFor="phone">Phone (optional)</label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    placeholder="+91 9XXXX XXXXX"
                    value={form.phone}
                    onChange={onChange}
                  />
                </div>

                <div className="field">
                  <label htmlFor="topic">What do you want help with?</label>
                  <select
                    id="topic"
                    name="topic"
                    value={form.topic}
                    onChange={onChange}
                    aria-label="Select a topic"
                  >
                    <option value="">Select…</option>
                    {topics.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="field">
                <label htmlFor="message">Tell us a bit more</label>
                <textarea
                  id="message"
                  name="message"
                  rows="6"
                  placeholder="What do you sell? How do people find you today? What would a ‘good result’ look like over the next few months?"
                  value={form.message}
                  onChange={onChange}
                  required
                />
              </div>

              <label className="check">
                <input
                  type="checkbox"
                  name="consent"
                  checked={form.consent}
                  onChange={onChange}
                />
                I agree that you can contact me about this enquiry.
              </label>

              <div className="actions">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={status.type === "loading"}
                >
                  {status.type === "loading" ? "Sending…" : "Send message"}
                </button>
                <a
                  className="btn btn-ghost"
                  href={brand.whatsapp}
                  target="_blank"
                  rel="noreferrer"
                >
                  Prefer WhatsApp? Chat there instead
                </a>
              </div>

              <div
                className={`form-status ${
                  status.type === "success"
                    ? "ok"
                    : status.type === "error"
                    ? "err"
                    : ""
                }`}
                role="status"
                aria-live="polite"
              >
                {status.msg}
              </div>
            </form>

            {/* Micro reassurance */}
            <div className="mini-faq">
              <div>
                <strong>What happens after I send this?</strong>
                <p>
                  We read your message, check your website or profiles (if shared), and reply with
                  simple suggestions: what we’d improve first and rough budget options.
                </p>
              </div>
              <div>
                <strong>Do I have to decide immediately?</strong>
                <p>
                  No. Our reply is for your clarity first. You can take your time, compare with
                  others, or come back later — no hard selling, no chasing.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Trust strip */}
        <div className="c2-strip">
          <span>We’ve recently helped businesses like</span>
          <div className="logos">
            <i className="logo">Local Clinic</i>
            <i className="logo">Home Services</i>
            <i className="logo">Coaching Studio</i>
            <i className="logo">E-commerce Brand</i>
            <i className="logo">Education Centre</i>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
